import {LinearGradient} from '@visx/gradient';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AnalyticsVisualizationColorPalette as ColorPalette} from 'graphql-client/__generated/graphqlGlobalTypes';
import {StringValueDropdownSelector} from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {
  CHART_COLORS_DISCRETE_PALETTE,
  CHART_COLORS_GRADIENT_PALETTE,
} from 'ui-library/charting/internals/utils/Theme';
import FieldLabel from 'ui-library/inputs/FieldLabel';

const COLOR_PALETTE_TO_COMPONENT: {[key in ColorPalette]: () => JSX.Element} = {
  [ColorPalette.DISCRETE]: DiscreteColorPalette,
  [ColorPalette.GRADIENT]: GradientColorPalette,
};
const DISCRETE_COLORS_TO_SHOW = 8;
const DISCRETE_PALETTE_BLOCK_HEIGHT = 16;
const DISCRETE_PALETTE_BLOCK_WIDTH = 16;
const DISCRETE_PALETTE_BLOCK_MARGIN = 6;
const GRADIENT_PALETTE_HEIGHT = 12;
const GRADIENT_PALETTE_WIDTH =
  DISCRETE_COLORS_TO_SHOW * DISCRETE_PALETTE_BLOCK_WIDTH +
  (DISCRETE_COLORS_TO_SHOW - 1) * DISCRETE_PALETTE_BLOCK_MARGIN;

function getOrderedColorPalettes(): Array<ColorPalette> {
  return [ColorPalette.DISCRETE, ColorPalette.GRADIENT];
}

function DiscreteColorPalette(): JSX.Element {
  return (
    <div className={css(styles.palette)}>
      {CHART_COLORS_DISCRETE_PALETTE.slice(0, DISCRETE_COLORS_TO_SHOW).map((color) => (
        <span key={color} className={css(styles.colorBlock)} style={{backgroundColor: color}} />
      ))}
    </div>
  );
}

function GradientColorPalette(): JSX.Element {
  return (
    <div className={css(styles.palette)}>
      <svg height={GRADIENT_PALETTE_HEIGHT} width={GRADIENT_PALETTE_WIDTH}>
        <LinearGradient
          id="gradient-color-palette"
          from={CHART_COLORS_GRADIENT_PALETTE[0]}
          to={CHART_COLORS_GRADIENT_PALETTE[1]}
          rotate={-90}
        />
        <rect
          fill="url(#gradient-color-palette)"
          height={GRADIENT_PALETTE_HEIGHT}
          width={GRADIENT_PALETTE_WIDTH}
        />
      </svg>
    </div>
  );
}

export interface ColorPaletteParameters {
  colorPalette: ColorPalette;
  seriesColumnName: string | null;
}

export interface ColorPaletteConfigEditorProps<ChartParameters extends ColorPaletteParameters> {
  parameters: ChartParameters;
  onUpdateParameters: (update: Partial<ChartParameters>) => void;
  isDisabled?: boolean;
}

export default function ColorPaletteConfigEditor<ChartParameters extends ColorPaletteParameters>({
  parameters,
  onUpdateParameters,
  isDisabled = false,
}: ColorPaletteConfigEditorProps<ChartParameters>): JSX.Element {
  function handleUpdateColorPalette(newColorPalette: ColorPalette): void {
    if (parameters.colorPalette !== newColorPalette) {
      onUpdateParameters({
        ...parameters,
        colorPalette: newColorPalette,
      });
    }
  }

  return (
    <div className="u-input-spacing-top">
      <FieldLabel label="Color palette" />
      <StringValueDropdownSelector
        allowNull={false}
        errorMessage={parameters.seriesColumnName == null ? 'Select series to configure color palette' : null}
        onChange={handleUpdateColorPalette}
        searchable={false}
        selectedValue={parameters.colorPalette}
        values={getOrderedColorPalettes()}
        valueToReadable={(colorPalette) => COLOR_PALETTE_TO_COMPONENT[colorPalette]()}
        isDisabled={isDisabled}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  colorBlock: {
    height: DISCRETE_PALETTE_BLOCK_HEIGHT,
    width: DISCRETE_PALETTE_BLOCK_WIDTH,
    marginRight: DISCRETE_PALETTE_BLOCK_MARGIN,
  },
  palette: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
});
